<template>
  <v-dialog v-model="dialog" width="500">
    <v-card>
      <v-container>
        <span>{{ getStoreName() }}</span>
        <h1>
          {{ getProductName() }}
        </h1>
        <br>
        <div class="flex flex-col space-y-2.5">
          <v-text-field v-model="inventory.quantity" label="quantity"
                        :disabled="!authPermissions.has('inventory_monitoring:set')" dense outlined hide-details />
          <div class="flex flex-wrap space-y-2.5 space-x-2.5">
            <v-btn v-if="authPermissions.has('inventory_monitoring:set')" color="primary" :loading="saving" dark
                   @click="save">
              Save
            </v-btn>
            <v-btn v-if="authPermissions.has('inventory_monitoring:recalculate')" color="purple" :loading="saving" dark
                   @click="recalculateInventory">
              Recalculate
            </v-btn>
            <v-btn v-if="authPermissions.has('inventory_monitoring_restore_point:create')" color="primary"
                   :loading="saving" dark @click="createRestorePoint">
              Create Restore Point
            </v-btn>
            <v-btn v-if="authPermissions.has('inventory_monitoring_restore_point:edit')" color="yellow darken-3"
                   :loading="saving" dark @click="restore">
              Restore
            </v-btn>
          </div>
        </div>
      </v-container>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapState } from 'vuex'
import appAxios from '@/extra-functions/axios.extra'
import { useWatchEffect } from '../../../../lib/utils'

const [watchEffect, clearWatchEffect] = useWatchEffect()

export default {
  name: 'InventoryEdit',
  data() {
    return {
      dialog: false,
      fetchingData: false,
      inventory: {},
      saving: false,
    }
  },
  computed: {
    ...mapState({
      authToken: state => state.auth.token,
      authPermissions: state => state.auth.userPermissions,
    }),
  },
  methods: {
    open(inventoryId) {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          this.dialog = true
          this.fetchingData = true
          const { data } = await appAxios(this.authToken).get(`/stock-inventories/${inventoryId}/edit`)
          this.inventory = data
        } catch (e) {
          console.error(e.message)
          this.$alertSwalError('oops something went wrong!')
        } finally {
          this.fetchingData = false
        }
      }, 'FetchingInventoryEdit')
    },
    async recalculateInventory() {
      try {
        const { stock_id: stockId, stock } = this.inventory ?? {}
        if (!stockId) throw new Error('stockId is undefined')
        if (!stock) throw new Error('stock is undefined')
        const confirm = await this.$confirm(`are you sure you want to recalculate inventory for ${stock.product_name}?`)
        if (!confirm) return
        if (this.saving) return
        this.saving = true
        await appAxios(this.authToken).post(`/stocks/${stockId}/recalculate-inventory`)
        this.$emit('save-success')
        this.open(this.inventory.id)
        this.$alertSwalSuccess(`successfully recalculated inventory for ${stock.product_name}`)
      } catch (e) {
        console.error(e.message)
        this.open(this.inventory.id)
        this.$alertSwalError('oops something went wrong')
      } finally {
        this.saving = false
      }
    },
    async createRestorePoint() {
      try {
        const { stock_id: stockId, stock } = this.inventory ?? {}
        if (!stockId) throw new Error('stockId is undefined')
        const confirm = await this.$confirm(`are you sure you want to create a restore point for ${stock.product_name}?`)
        if (!confirm) return
        if (this.saving) return
        this.saving = true
        await appAxios(this.authToken).post(`/stocks/${stockId}/restore-point`)
        if (!stock) throw new Error('stock is undefined')
        this.$alertSwalSuccess(`successfully created a inventory restore point for ${stock.product_name}`)
        this.open(this.inventory.id)
        this.$emit('save-success')
      } catch (e) {
        console.error(e.message)
        this.$alertSwalError('oops something went wrong')
      } finally {
        this.saving = false
      }
    },
    async restore() {
      try {
        const confirm = await this.$confirm('are you sure you want to revert to the last restore?')
        if (!confirm) return
        if (this.saving) return
        this.saving = true
        const { quantity, id: inventoryId } = this.inventory
        await appAxios(this.authToken).put(`/stock-inventories/${inventoryId}/restore`, { quantity })
        this.$emit('save-success')
        this.open(this.inventory.id)
      } catch (e) {
        console.error(e.message)
        this.$alertSwalError('oops something went wrong!')
      } finally {
        this.saving = false
      }
    },
    async save() {
      try {
        const confirm = await this.$confirm('are you sure you want to save the changes made to quantity?')
        if (!confirm) return
        if (this.saving) return
        this.saving = true
        const { quantity, id: inventoryId } = this.inventory
        await appAxios(this.authToken).put(`/stock-inventories/${inventoryId}/quantity`, { quantity })
        this.$emit('save-success')
      } catch (e) {
        console.error(e.message)
        this.$alertSwalError('oops something went wrong!')
      } finally {
        this.saving = false
      }
    },
    getStoreName() {
      return this.inventory?.store?.name ?? ''
    },
    getProductName() {
      return this.inventory?.stock?.product_name ?? ''
    },
  },
}
</script>
